import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSyncStatus } from '../api/trainer';
import { SyncJob, SyncStatus } from '../api/types';

/** Poll interval while a job runs, and while nothing is known to run (a scheduled sync can start any time). */
const RUNNING_POLL_MS = 2000;
const IDLE_POLL_MS = 30000;

export interface JobStatus {
  status: SyncStatus | null;
  job: SyncJob | null;
  running: boolean;
  /** Takes the status a POST /sync or /records/recompute answered with, so the page shows the job at once. */
  update: (status: SyncStatus) => void;
  /** Reads the status now. */
  refresh: () => void;
}

const noop = () => undefined;
const DEFAULT: JobStatus = { status: null, job: null, running: false, update: noop, refresh: noop };

const JobStatusContext = createContext<JobStatus>(DEFAULT);

/** The account's sync / recompute status, shared by the banner and the pages. Polls faster while a job runs. */
export const JobStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const running = status?.job?.state === 'RUNNING';

  useEffect(() => {
    let active = true;
    const id = window.setTimeout(
      () => {
        getSyncStatus()
          .then(next => {
            if (active && mounted.current) setStatus(next);
          })
          .catch(() => undefined)
          .finally(() => {
            if (active && mounted.current) setTick(t => t + 1);
          });
      },
      // The first read happens at once; later ones wait.
      tick === 0 ? 0 : running ? RUNNING_POLL_MS : IDLE_POLL_MS
    );
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [tick, running]);

  const update = useCallback((next: SyncStatus) => setStatus(next), []);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  const value = useMemo<JobStatus>(
    () => ({ status, job: status?.job ?? null, running, update, refresh }),
    [status, running, update, refresh]
  );
  return <JobStatusContext.Provider value={value}>{children}</JobStatusContext.Provider>;
};

export const useJobStatus = (): JobStatus => useContext(JobStatusContext);
